import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { templates, applyTemplateConfig, CustomConfig } from '../lib/checkoutUtils';
import { Loader2, Copy, CheckCircle2, X, AlertCircle } from 'lucide-react';

function formatCpf(value: string): string {
    const d = value.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function isValidCpf(cpf: string): boolean {
    const d = cpf.replace(/\D/g, '');
    if (d.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(d)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(d[i]) * (10 - i);
    let rest = (sum * 10) % 11;
    if (rest === 10) rest = 0;
    if (rest !== parseInt(d[9])) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(d[i]) * (11 - i);
    rest = (sum * 10) % 11;
    if (rest === 10) rest = 0;
    return rest === parseInt(d[10]);
}

export const PublicCheckout: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [checkout, setCheckout] = useState<{ template_id: string; config: CustomConfig } | null>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const [selectedPlan, setSelectedPlan] = useState<{ name: string; price: string; link?: string; planKey?: string } | null>(null);
    const [cpf, setCpf] = useState('');
    const [pixCode, setPixCode] = useState<string | null>(null);
    const [pixId, setPixId] = useState<string | null>(null);
    const [paymentRef, setPaymentRef] = useState<string | null>(null);
    const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
    const [generating, setGenerating] = useState(false);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
    const [copied, setCopied] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(false);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const cpfDigits = cpf.replace(/\D/g, '');
    const cpfValid = cpfDigits.length === 11 && isValidCpf(cpfDigits);

    const closeModal = useCallback(() => {
        setSelectedPlan(null);
        setCpf('');
        setPixCode(null);
        setPixId(null);
        setPaymentRef(null);
        setPaymentStatus(null);
        setGenerating(false);
        setCopied(false);
        if (pollRef.current) clearInterval(pollRef.current);
    }, []);

    useEffect(() => {
        const handler = (e: MessageEvent) => {
            if (e.data?.type === 'hotlize-plan-selected') {
                setSelectedPlan({
                    name: e.data.name,
                    price: e.data.price,
                    link: e.data.link || '',
                    planKey: e.data.plan || undefined
                });
                setCpf('');
                setPixCode(null);
                setPixId(null);
                setPaymentRef(null);
                setPaymentStatus(null);
                setCopied(false);
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, []);

    const handleGeneratePix = async () => {
        if (!selectedPlan || !cpfValid || !slug) return;
        if (!selectedPlan.planKey) {
            setToast({ msg: 'Plano inválido. Selecione novamente no checkout.', type: 'error' });
            return;
        }
        setGenerating(true);
        setToast(null);
        try {
            const payload: { slug: string; planKey: string; cpf: string; user_id?: string; name?: string; email?: string; phone?: string } = {
                slug,
                planKey: selectedPlan.planKey,
                cpf: cpfDigits
            };
            try {
                const saved = localStorage.getItem('hotlize_user');
                if (saved) {
                    const u = JSON.parse(saved);
                    if (u?.id) payload.user_id = u.id;
                }
            } catch (_) { /* ignore */ }
            const res = await api.post('/public/create-pix', payload);
            // Mesmo formato dos planos (Assinar): code, pixId, paymentRef; fallback para data
            const code = res.data?.code ?? res.data?.data?.pixKey ?? res.data?.data?.copiaCola;
            const id = res.data?.pixId ?? res.data?.data?.id;
            const ref = res.data?.paymentRef ?? res.data?.data?.paymentRef;
            if (res.data?.success && (code || id)) {
                setPixCode(code || null);
                setPixId(id || null);
                setPaymentRef(ref || null);
                setPaymentStatus('waiting');
            } else {
                setToast({ msg: 'Falha ao gerar PIX.', type: 'error' });
            }
        } catch (err: any) {
            setToast({ msg: err.response?.data?.message || 'Erro ao gerar PIX.', type: 'error' });
        } finally {
            setGenerating(false);
        }
    };

    const applyCheckoutStatus = useCallback((paid: boolean) => {
        if (!paid) return;
        setPaymentStatus('paid');
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        setToast({ msg: 'Pagamento confirmado! Redirecionando...', type: 'success' });
        const redirectUrl = selectedPlan?.link;
        if (redirectUrl && redirectUrl !== '#' && redirectUrl.trim()) {
            setTimeout(() => { window.location.href = redirectUrl; }, 2000);
        }
    }, [selectedPlan?.link]);

    const parseStatusPaid = useCallback((res: { data?: { status?: string; data?: { transaction?: { status?: string } } } }) => {
        const raw = (res.data?.status ?? res.data?.data?.transaction?.status ?? '').toString();
        const status = raw.toUpperCase();
        return raw.toLowerCase() === 'verified' || ['PAID', 'COMPLETED', 'APPROVED', 'CONFIRMED', 'VERIFIED'].includes(status);
    }, []);

    const fetchCheckoutStatus = useCallback(async (): Promise<boolean> => {
        const idsToTry = [pixId, paymentRef].filter(Boolean) as string[];
        for (const id of idsToTry) {
            if (!id) continue;
            try {
                const res = await api.get(`/public/checkout-status/${encodeURIComponent(id)}`);
                if (parseStatusPaid(res)) return true;
            } catch {
                // try next id
            }
        }
        return false;
    }, [pixId, paymentRef, parseStatusPaid]);

    useEffect(() => {
        if (!pixId || (paymentStatus !== 'waiting' && paymentStatus !== 'pending')) return;
        pollRef.current = setInterval(async () => {
            try {
                const paid = await fetchCheckoutStatus();
                if (paid) applyCheckoutStatus(true);
            } catch {
                // keep polling
            }
        }, 3000);
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [pixId, paymentStatus, fetchCheckoutStatus, applyCheckoutStatus]);

    const checkPaymentStatusNow = async () => {
        if (!pixId || (paymentStatus !== 'waiting' && paymentStatus !== 'pending')) return;
        setCheckingStatus(true);
        setToast(null);
        try {
            const paid = await fetchCheckoutStatus();
            if (paid) {
                applyCheckoutStatus(true);
            } else {
                setPaymentStatus('pending');
                setToast({ msg: 'Pagamento ainda não identificado. Aguarde alguns instantes ou confira se o PIX foi pago.', type: 'error' });
            }
        } catch {
            setToast({ msg: 'Não foi possível verificar. Tente novamente em instantes.', type: 'error' });
        } finally {
            setCheckingStatus(false);
        }
    };

    const copyPixCode = () => {
        if (pixCode) {
            navigator.clipboard.writeText(pixCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    useEffect(() => {
        const fetchCheckout = async () => {
            try {
                const res = await api.get(`/public/checkout/${slug}`);
                setCheckout(res.data);
                setLoading(false);
            } catch (err: any) {
                setError(err.response?.data?.error || 'Checkout não encontrado.');
                setLoading(false);
            }
        };
        if (slug) fetchCheckout();
    }, [slug]);

    const handleIframeLoad = () => {
        if (iframeRef.current && checkout) {
            const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
            const template = templates.find(t => t.id === checkout.template_id);
            if (doc && template) {
                applyTemplateConfig(doc, template, checkout.config);

                if ((doc.defaultView as any).restartMainTimer && checkout.config.features.timerMinutes) {
                    (doc.defaultView as any).restartMainTimer(checkout.config.features.timerMinutes);
                }

                if ((doc.defaultView as any).restartNotifications && checkout.config.features.notificationMode === 'manual') {
                    (doc.defaultView as any).customNotificationNames = checkout.config.features.manualNames;
                    (doc.defaultView as any).restartNotifications();
                }
            }
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white p-4">
                <Loader2 className="w-12 h-12 text-orange-500 animate-spin mb-4" />
                <p className="text-xl font-medium">Carregando Checkout...</p>
                <p className="text-gray-500 mt-2">Hotlize Custom Checkout System</p>
            </div>
        );
    }

    if (error || !checkout) {
        return (
            <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white p-4 text-center">
                <div className="bg-red-500/10 border border-red-500/20 p-8 rounded-3xl max-w-md">
                    <h1 className="text-3xl font-bold text-red-500 mb-4">Ops!</h1>
                    <p className="text-gray-300 text-lg mb-6">{error || 'Não foi possível carregar este checkout.'}</p>
                    <a href="https://hotlize.com" className="inline-block bg-white text-black px-8 py-3 rounded-full font-bold hover:bg-gray-200 transition-colors">
                        Voltar para Hotlize
                    </a>
                </div>
            </div>
        );
    }

    const template = templates.find(t => t.id === checkout.template_id);
    if (!template) return <div>Template inválido.</div>;

    return (
        <div className="min-h-screen bg-black relative">
            <iframe
                ref={iframeRef}
                src={`${template.basePath}${template.files.html}`}
                className="w-full h-screen border-none"
                onLoad={handleIframeLoad}
                title="Checkout"
            />

            {selectedPlan && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
                    <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
                        <div className="flex items-center justify-between px-6 pt-5 pb-3">
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">{selectedPlan.name}</h2>
                            </div>
                            <button onClick={closeModal} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>

                        <div className="px-6 pb-6">
                            {toast && (
                                <div className={`flex items-center gap-2 p-3 rounded-xl mb-4 text-sm ${toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                                    {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                                    {toast.msg}
                                </div>
                            )}

                            {paymentStatus === 'paid' ? (
                                <div className="text-center py-8">
                                    <CheckCircle2 size={48} className="text-green-500 mx-auto mb-3" />
                                    <h3 className="text-xl font-bold text-gray-900 mb-1">Pagamento Confirmado!</h3>
                                    <p className="text-gray-500 text-sm">
                                        {selectedPlan?.link && selectedPlan.link !== '#' && selectedPlan.link.trim()
                                            ? 'Redirecionando...'
                                            : 'Seu acesso será liberado em instantes.'}
                                    </p>
                                </div>
                            ) : pixCode ? (
                                <div>
                                    <div className="flex justify-center mb-4">
                                        <img
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(pixCode)}`}
                                            alt="QR Code PIX"
                                            className="rounded-xl border border-gray-200"
                                            width={200}
                                            height={200}
                                        />
                                    </div>
                                    <p className="text-sm text-gray-600 mb-3 text-center">Escaneie o QR Code ou copie o código PIX abaixo:</p>
                                    <div
                                        onClick={copyPixCode}
                                        className="bg-gray-50 border border-gray-200 rounded-xl p-4 cursor-pointer hover:bg-gray-100 transition-colors relative"
                                    >
                                        <p className="text-xs text-gray-800 break-all font-mono pr-8" style={{ wordBreak: 'break-all' }}>
                                            {pixCode}
                                        </p>
                                        <div className="absolute top-3 right-3">
                                            {copied ? <CheckCircle2 size={18} className="text-green-500" /> : <Copy size={18} className="text-gray-400" />}
                                        </div>
                                    </div>
                                    <button
                                        onClick={copyPixCode}
                                        className="w-full mt-3 py-3 rounded-xl font-bold text-sm transition-all bg-[#FF8C5A] hover:bg-[#FF7B4A] text-white"
                                    >
                                        {copied ? '✓ Copiado!' : 'Copiar Código PIX'}
                                    </button>
                                    {(paymentStatus === 'waiting' || paymentStatus === 'pending') && (
                                        <>
                                            <p className="text-amber-600 text-xs mt-4 mb-1">
                                                {paymentStatus === 'waiting'
                                                    ? 'Aguardando confirmação do pagamento...'
                                                    : 'Pagamento ainda não identificado. Clique em Já paguei para verificar.'}
                                            </p>
                                            <button
                                                type="button"
                                                onClick={checkPaymentStatusNow}
                                                disabled={checkingStatus}
                                                className="w-full mt-2 py-3 rounded-xl font-bold text-sm transition-all disabled:opacity-50 bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                                            >
                                                {checkingStatus ? 'Verificando...' : 'Já paguei'}
                                            </button>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Valor</label>
                                    <div className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 text-sm bg-gray-50 mb-3 font-medium">
                                        {selectedPlan.price || '—'}
                                    </div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">CPF</label>
                                    <input
                                        type="text"
                                        value={cpf}
                                        onChange={e => setCpf(formatCpf(e.target.value))}
                                        placeholder="000.000.000-00"
                                        maxLength={14}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C5A]/50 focus:border-[#FF8C5A]"
                                    />
                                    {cpfDigits.length === 11 && !cpfValid && (
                                        <p className="text-red-500 text-xs mt-1">CPF inválido</p>
                                    )}
                                    <button
                                        onClick={handleGeneratePix}
                                        disabled={!cpfValid || generating}
                                        className="w-full mt-4 py-3.5 rounded-xl font-bold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-[#FF9B6A] to-[#FF7B5A] hover:from-[#FF8C5A] hover:to-[#FF6B4A] text-white shadow-lg"
                                    >
                                        {generating ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <Loader2 size={16} className="animate-spin" /> Gerando PIX...
                                            </span>
                                        ) : 'Gerar PIX'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
